import clsx from "clsx";
import { motion, useInView, wrap } from "framer-motion";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import { useInterval } from "react-use";

import { useDocumentVisible } from "../../../hooks/use-document-visible";
import { Icon } from "../../icon";
import { HeroSliderDots } from "./hero-slider-dots";
import { HeroSliderTitle } from "./hero-slider-title";
import { HeroSliderItem } from "./types";

export interface HeroSliderProps {
  /**
   * Hero Slider slide items
   */
  slides: HeroSliderItem[];
  /**
   * Delay interval for auto-slide (if `0` auto-slide is disabled)
   */
  delay?: number;
  /**
   * Called when clicking on bouncing arrow down at left-bottom
   */
  onScrollDown?: () => void;
}

export const HeroSlider: FC<HeroSliderProps> = ({ slides, delay = 3, onScrollDown }) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const visible = useDocumentVisible();
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRefs = slides.map(() => createRef<HTMLVideoElement>());
  const isInView = useInView(rootRef);
  const slidesCount = slides.length;

  useEffect(() => {
    if (delay > 0 && visible && isInView) {
      const interval = setInterval(() => {
        setActiveSlideIndex((currentSlide) => wrap(0, slidesCount, currentSlide + 1));
      }, delay * 1000);

      return () => {
        clearInterval(interval);
        const videoEl = videoRefs[activeSlideIndex].current;
        if (videoEl) {
          resetVideo(videoEl);
        }
      };
    }
    // Listen for `activeSlideIndex` changes helps to reset interval when manually change slide
  }, [delay, slidesCount, activeSlideIndex, visible, isInView, videoRefs]);

  const resetVideo = (videoEl: HTMLVideoElement) => {
    videoEl.pause();
    videoEl.currentTime = 0;
  };

  const changeActiveVideo = useCallback(
    (activeIndex: number) => {
      videoRefs.forEach((video, index) => {
        const videoEl = video.current;
        if (!videoEl) return;

        if (activeIndex === index) {
          videoEl.play().catch(console.warn);
        } else {
          resetVideo(videoEl);
        }
      });
    },
    [videoRefs]
  );

  useEffect(() => {
    changeActiveVideo(activeSlideIndex);
  }, [activeSlideIndex, changeActiveVideo, videoRefs]);

  const [loadedSlidesIds, setLoadedSlidesIds] = useState<Set<string>>(
    new Set(slides.slice(0, 1).map((s) => s.id))
  );
  // load slides assets progressively
  useInterval(() => {
    if (loadedSlidesIds.size > slides.length - 1) return;
    setLoadedSlidesIds((loadedSlidesIds) => {
      loadedSlidesIds.add(slides[loadedSlidesIds.size].id);
      return new Set(loadedSlidesIds);
    });
  }, 750);

  // load slides assets on change activeSlideIndex (user is navigating via dots)
  useEffect(() => {
    setLoadedSlidesIds((loadedSlidesIds) => {
      loadedSlidesIds.add(slides[activeSlideIndex].id);
      const nextSlideId = slides[activeSlideIndex + 1]?.id;
      if (nextSlideId) loadedSlidesIds.add(nextSlideId);
      return new Set(loadedSlidesIds);
    });
  }, [activeSlideIndex, slides]);

  if (!slidesCount) {
    return null;
  }

  const activeSlide = slides[activeSlideIndex];

  return (
    <div
      className="flex relative w-full h-[100vh] overflow-hidden bg-neutral z-30 section-overlap-bottom"
      ref={rootRef}
    >
      <div className="absolute top-1/2 translate-y-[-50%] text-white z-10 w-full">
        <HeroSliderTitle {...activeSlide} index={activeSlideIndex} />
      </div>

      {slides.map(({ video, image, id }, i) => {
        const isActiveSlide = i === activeSlideIndex;
        const isLoaded = loadedSlidesIds.has(id);
        return (
          <React.Fragment key={id}>
            {video && isLoaded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={isActiveSlide ? { opacity: 1 } : { opacity: 0 }}
                key={`video-${id}`}
                className={clsx(
                  "overflow-hidden absolute left-0 ",
                  image ? "h-1/2 w-full lg:h-full lg:w-[60%]" : "w-full h-full"
                )}
              >
                <div className="absolute w-full h-full bg-black opacity-50" />
                <video
                  playsInline
                  src={video}
                  controls={false}
                  controlsList="nofullscreen"
                  muted
                  loop
                  preload="auto"
                  className="object-cover object-center h-full w-full"
                  ref={videoRefs[i]}
                />
              </motion.div>
            )}
            {image && isLoaded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isActiveSlide ? 1 : 0 }}
                key={`image-${id}`}
                className={clsx(
                  "absolute overflow-hidden",
                  video ? "h-1/2 w-full lg:w-[40%] lg:h-full bottom-0 lg:right-0" : "w-full h-full"
                )}
              >
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: slides.length > 1 && isActiveSlide ? 1.2 : 1 }}
                  transition={{
                    duration: isActiveSlide ? delay : 0,
                    ease: "linear",
                    delay: isActiveSlide ? 0 : delay,
                  }}
                  className="w-full h-full"
                >
                  <GatsbyImage
                    image={image}
                    alt=""
                    objectFit="cover"
                    loading="eager"
                    className="w-full h-full"
                  />
                </motion.div>
              </motion.div>
            )}
          </React.Fragment>
        );
      })}

      <div className="absolute z-20 bottom-16 lg:bottom-10 left-8 lg:left-14 flex flex-col gap-20">
        {slides.length > 1 && (
          <HeroSliderDots
            activeIndex={activeSlideIndex}
            count={slides.length}
            onSelect={setActiveSlideIndex}
          />
        )}
        <Icon
          icon="long-arrow-down"
          className={clsx("text-white ml-[-10px] animate-bounce hidden lg:block", {
            "cursor-pointer": !!onScrollDown,
          })}
          size={36}
          onClick={onScrollDown}
        />
      </div>
    </div>
  );
};
